import React, { useState, useEffect,useRef } from 'react';
import { Menu, X, Phone, Mail, Clock, Wrench,Scissors, Home, AlertCircle, Zap, Star, Award, Shield, Calculator, Search,Truck, ArrowLeft, Package, CloudRain, ChevronRight, ArrowRight, Users, Building, CheckCircle, TrendingUp, ChevronLeft, Grid, List, ShoppingCart, HardHat, PaintBucket, Droplets, Thermometer, Layers, Send, Calendar, DollarSign, Bath, Target, Hammer, Handshake, Cpu, Microscope, Quote, MapPin} from 'lucide-react';

import { Trabajo, Producto, Servicio, PhoneNumber } from '../types';
import { apiCall, API_BASE_URL, formatPrecio } from '../utils';

// ==================== PÁGINA DE CONTACTO ====================
export default function ContactoPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    tipo_servicio: '',
    descripcion: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState<{tipo: 'success' | 'error', texto: string} | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMensaje(null);

    try {
      const response = await fetch(`${API_BASE_URL}/contacto/presupuesto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMensaje({
          tipo: 'success',
          texto: '¡Solicitud enviada! Te contactaremos pronto.'
        });
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          tipo_servicio: '',
          descripcion: ''
        });
      } else if (response.status === 429) {
        // Límite anti-spam alcanzado
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : 'Has enviado demasiadas solicitudes. Inténtalo más tarde o llámanos al 953 03 95 67.'
        );
      } else {
        throw new Error(
          typeof data.detail === 'string' ? data.detail : 'Error al enviar la solicitud'
        );
      }
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: error instanceof Error && error.message
          ? error.message
          : 'No se ha podido enviar. Inténtalo de nuevo o llámanos al 953 03 95 67.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 to-blue-700 text-white py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-blue-300"></div>
            <span className="text-sm font-semibold text-blue-200 uppercase tracking-widest">Respuesta en menos de 48 h</span>
            <div className="h-[2px] w-10 bg-blue-300"></div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Contacta con Nosotros
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Cuéntanos tu proyecto y te preparamos un presupuesto sin compromiso
          </p>
        </div>
      </section>

      {/* Información de contacto */}
      <section className="py-12 -mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Teléfonos */}
            <div className="bg-white rounded-2xl shadow-xl p-6 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-4">
                <Phone className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Teléfonos</h3>
              <div className="space-y-2">
                <a href="tel:+34953039567" className="block text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                  953 03 95 67
                  <span className="block text-xs text-gray-500 font-normal">Oficina</span>
                </a>
                <a href="tel:+34650441396" className="block text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                  650 44 13 96
                  <span className="block text-xs text-gray-500 font-normal">Móvil</span>
                </a>
                <a href="tel:+34609541774" className="block text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                  609 54 17 74
                  <span className="block text-xs text-gray-500 font-normal">José Luis</span>
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="bg-white rounded-2xl shadow-xl p-6 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-4">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Email</h3>
              <div className="space-y-2">
                <a
                  href="mailto:construccionesperezrosa@gmail.com"
                  className="block text-blue-600 font-semibold hover:text-blue-700 transition-colors text-sm"
                >
                  construccionesperezrosa
                  <br />@gmail.com
                </a>
                <a
                  href="mailto:info@joseluisconstrucciones.com"
                  className="block text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm"
                >
                  info@joseluisconstrucciones.com
                </a>
              </div>
              <p className="text-sm text-gray-500 mt-2">Respuesta en 24h</p>
            </div>

            {/* Ubicación */}
            <div className="bg-white rounded-2xl shadow-xl p-6 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-4">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Ubicación</h3>
              <a
                href="https://maps.google.com/?q=Calle+Ingeniero+García+Pimentel,+37,+Martos,+Jaen"
                className="text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                C. Ing. García Pimentel, 37
              </a>
              <p className="text-sm text-gray-500 mt-1">23600 Martos, Jaén</p>
            </div>

            {/* Horario */}
            <div className="bg-white rounded-2xl shadow-xl p-6 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-4">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Horario</h3>
              <div className="text-gray-900">
                <p className="font-semibold">Lunes a Viernes</p>
                <p className="text-sm text-gray-600">Mañana: <span className="font-medium text-gray-800">9:00 - 14:00</span></p>
                <p className="text-sm text-gray-600">Tarde: <span className="font-medium text-gray-800">17:00 - 20:00</span></p>
                <p className="text-sm text-gray-500 mt-2">Sáb. y Dom. cerrado</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Formulario y mapa */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Formulario */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Solicita tu Presupuesto
              </h2>
              
              {mensaje && (
                <div className={`mb-6 p-4 rounded-lg ${
                  mensaje.tipo === 'success' 
                    ? 'bg-green-100 border border-green-400 text-green-700' 
                    : 'bg-red-100 border border-red-400 text-red-700'
                }`}>
                  <p className="font-medium">{mensaje.texto}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    id="nombre"
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    minLength={3}
                    maxLength={100}
                    autoComplete="name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      id="telefono"
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      required
                      minLength={9}
                      maxLength={20}
                      autoComplete="tel"
                      placeholder="+34 666 123 456"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      autoComplete="email"
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="tipo_servicio" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de servicio *
                  </label>
                  <select 
                    id="tipo_servicio"
                    name="tipo_servicio"
                    value={formData.tipo_servicio}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Reforma integral">Reforma integral</option>
                    <option value="Cocina">Cocina</option>
                    <option value="Baño">Baño</option>
                    <option value="Pintura">Pintura</option>
                    <option value="Fontanería">Fontanería</option>
                    <option value="Electricidad">Electricidad</option>
                    <option value="Albañilería">Albañilería</option>
                    <option value="Carpintería">Carpintería</option>
                    <option value="Aerotermia">Aerotermia</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
                    Describe tu proyecto *
                  </label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleInputChange}
                    required
                    minLength={10}
                    maxLength={1000}
                    rows={4}
                    placeholder="Cuéntanos qué necesitas: medidas aproximadas, tipo de trabajo, preferencias..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${formData.descripcion.length > 900 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {formData.descripcion.length} / 1000 caracteres
                  </p>
                </div>

                {/* Consentimiento RGPD */}
                <div className="flex items-start bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <div className="flex items-center h-5">
                    <input
                      id="privacy-policy"
                      name="privacy-policy"
                      type="checkbox"
                      required
                      className="focus:ring-blue-500 h-5 w-5 text-blue-600 border-gray-300 rounded cursor-pointer focus:outline-none"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="privacy-policy" className="text-gray-700 cursor-pointer select-none">
                      He leído y acepto la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-semibold">Política de Privacidad</a>. *
                    </label>
                  </div>
                </div>

                <div className="text-xs text-gray-500">
                  <p>* Campos obligatorios. Tus datos serán tratados únicamente para responder a tu consulta según el RGPD.</p>
                </div>
                
                <button 
                  type="submit" 
                  disabled={loading}
                  className={`w-full py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Enviar Solicitud
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Mapa e información adicional */}
            <div className="space-y-8">
              {/* Mapa */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden h-[400px]">
                <iframe
                  src="https://maps.google.com/maps?q=Calle%20Ingeniero%20Garc%C3%ADa%20Pimentel%2C%2037%2C%20Martos%2C%20Jaen&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>

              {/* Información adicional */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-4">¿Por qué elegirnos?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Presupuesto sin compromiso en 24-48h</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
                    <span>{new Date().getFullYear() - 1998} años de experiencia en el sector</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Garantía por escrito en todos los trabajos</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Financiación adaptada a sus necesidades</span>
                  </li>
                </ul>

                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="font-semibold mb-2">¿Prefieres llamarnos?</p>
                  <a
                    href="tel:+34953039567"
                    className="text-2xl font-bold hover:underline flex items-center"
                  >
                    <Phone className="h-6 w-6 mr-2" />
                    953 03 95 67
                  </a>
                  <a
                    href="tel:+34650441396"
                    className="text-lg font-medium hover:underline flex items-center mt-2 text-blue-200"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    650 44 13 96
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
